import { useCallback, useEffect, useMemo, useState } from "react";
import { opsApi, errText } from "@/lib/api";
import { OpsCtx, Btn, inputCls } from "./ui";
import Overview from "./Overview";
import Players from "./Players";
import TestInbox from "./TestInbox";
import Templates from "./Templates";
import Controls from "./Controls";
import Health from "./Health";
import Content from "./Content";
import GoLive from "./GoLive";
import Artifacts from "./Artifacts";
import DeployBanner from "./DeployBanner";
import DeniedPage from "@/components/world1/DeniedPage";

const TABS = [["overview", "Overview"], ["players", "Players"], ["test", "Test inbox"], ["templates", "M emails"],
  ["artifacts", "Artifacts"], ["controls", "Controls"], ["health", "System health"], ["content", "Content slots"], ["golive", "Go-live"]];

function Login({ api, onDone }) {
  const [f, setF] = useState({ email: "", password: "", code: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/login", f); onDone(); } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="ops flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm space-y-3 rounded border border-[#243144] bg-[#131a26] p-6">
        <h1 className="text-sm font-semibold tracking-wider text-slate-300">OPS</h1>
        <input data-testid="admin-email-input" className={inputCls} placeholder="Email" autoComplete="username" value={f.email} onChange={set("email")} />
        <input data-testid="admin-password-input" className={inputCls} type="password" placeholder="Password" autoComplete="current-password" value={f.password} onChange={set("password")} />
        <input data-testid="admin-auth-code-input" className={`${inputCls} mono tracking-[0.4em]`} inputMode="numeric" maxLength={6} placeholder="Authenticator code" value={f.code} onChange={set("code")} />
        {err && <div data-testid="admin-login-error" className="text-xs text-red-400">{err}</div>}
        <Btn type="submit" className="w-full py-2" disabled={busy} data-testid="admin-login-submit">{busy ? "…" : "Sign in"}</Btn>
      </form>
    </div>
  );
}

export default function AdminApp({ slug }) {
  const api = useMemo(() => opsApi(slug), [slug]);
  const [me, setMe] = useState(undefined);
  const [tab, setTab] = useState("overview");
  const [focusPlayer, setFocusPlayer] = useState(null);

  const check = useCallback(() => api.get("/me").then((r) => setMe(r.data)).catch((e) => setMe(e?.response?.status === 404 ? 404 : null)), [api]);
  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    const id = api.interceptors.response.use((r) => r, (e) => { if (e?.response?.status === 401 && me) setMe(null); return Promise.reject(e); });
    return () => api.interceptors.response.eject(id);
  }, [api, me]);

  if (me === undefined) return <div className="ops" />;
  if (me === 404) return <DeniedPage />;
  if (!me) return <Login api={api} onDone={check} />;

  const openPlayer = (id) => { setFocusPlayer(id); setTab("players"); };
  const logout = async () => { await api.post("/logout"); setMe(null); };
  const Page = { overview: Overview, players: Players, test: TestInbox, templates: Templates, artifacts: Artifacts, controls: Controls, health: Health, content: Content, golive: GoLive }[tab];

  return (
    <OpsCtx.Provider value={{ api, me, openPlayer, focusPlayer, setFocusPlayer }}>
      <div className="ops flex flex-col" data-testid="admin-dashboard">
        <DeployBanner />
        <header className="sticky top-0 z-20 flex flex-wrap items-center gap-1 border-b border-[#243144] bg-[#0b0f17]/95 px-3 py-2 backdrop-blur">
          <span className="mr-3 text-xs font-semibold tracking-widest text-sky-400">333SOS OPS</span>
          {TABS.map(([k, label]) => (
            <button key={k} type="button" data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`rounded px-2.5 py-1 text-xs transition-colors ${tab === k ? "bg-sky-500/20 text-sky-300" : "text-slate-400 hover:text-slate-200"}`}>{label}</button>
          ))}
          <span className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
            <span data-testid="admin-env-badge" className="mono">{me.app_env} · storage:{me.storage} · postmark:{me.postmark ? "on" : "off"}</span>
            <Btn variant="ghost" onClick={logout} data-testid="admin-logout-button">Log out</Btn>
          </span>
        </header>
        <main className="flex-1 p-3 sm:p-4"><Page /></main>
        <footer data-testid="admin-spec-version" className="border-t border-[#243144] px-4 py-2 text-[11px] text-slate-500">Spec {me.spec_version}</footer>
      </div>
    </OpsCtx.Provider>
  );
}
